import ActivityDetails from 'dashboard/marketplace/ActivityDetails';
import PageSubHeading from 'dashboard/marketplace/PageSubHeading';
import { ActivityType } from 'interfaces/common.interface';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { mergeRoutineActivityMatchIds } from 'utils/habit-pack.util';
import { t } from 'services/i18n.service';
import { addHabitToRoutine } from 'store/reducer/setting/slice';
import BaseButton from 'components/ui/BaseButton';
import { habitPackActivityListSelector } from 'store/reducer/habit-pack/selectors';
import PlusCircle from 'assets/icons/PlusCircle';
import { toast } from 'react-toastify';
import Tooltip from 'components/common/Tooltip';
import COLOR from 'constants/color';
interface Props {
  allowAddToRoutine?: boolean;
  category: 'morning' | 'evening' | 'break' | 'standalone';
  title: string;
  habitActivities: ActivityType[];
}

const getPackHabitAddButtonState = (
  packActivityId: string,
  savedActivityIds: Set<string>,
  currentActivityIds: Set<string>
) => {
  if (savedActivityIds.has(packActivityId)) {
    return { labelKey: 'market.already_added' as const, disabled: true };
  }
  if (currentActivityIds.has(packActivityId)) {
    return { labelKey: 'market.added_to_be_saved' as const, disabled: true };
  }
  return { labelKey: 'market.add_this_habit' as const, disabled: false };
};

const HabitPackActivityList: React.FC<Props> = ({
  allowAddToRoutine = false,
  category,
  title,
  habitActivities
}) => {
  const dispatch = useAppDispatch();
  const {
    areSettingsValid,
    currentMorning,
    currentEvening,
    currentBreak,
    currentStandalone,
    previousMorning,
    previousEvening,
    previousBreak,
    previousStandalone
  } = useAppSelectorShallow(habitPackActivityListSelector);

  const savedActivityIds = useMemo(
    () =>
      mergeRoutineActivityMatchIds(
        previousMorning,
        previousEvening,
        previousBreak,
        previousStandalone
      ),
    [previousMorning, previousEvening, previousBreak, previousStandalone]
  );

  const currentActivityIds = useMemo(
    () =>
      mergeRoutineActivityMatchIds(
        currentMorning,
        currentEvening,
        currentBreak,
        currentStandalone
      ),
    [currentMorning, currentEvening, currentBreak, currentStandalone]
  );

  const handleAdd = (activity: ActivityType) => {
    if (!areSettingsValid) {
      toast.error(
        t(
          'you_can_only_install_or_uninstall_habit_packs_when_your_routine_settings_are_valid'
        ),
        { position: 'bottom-center' }
      );
      return;
    }

    dispatch(
      addHabitToRoutine({
        routine: category,
        activity
      })
    );
  };

  return (
    <div className='w-full h-fit flex flex-col items-center ~gap-2/4 py-2 ~px-2/4'>
      <PageSubHeading text={title} />
      {habitActivities.map((activity) => {
        const { labelKey, disabled } = getPackHabitAddButtonState(
          activity.id,
          savedActivityIds,
          currentActivityIds
        );

        return (
          <div
            key={activity.id}
            className='w-full flex items-center justify-between ~gap-5/7 border-b py-2 border-gray-300 dark:border-gray-100'>
            <ActivityDetails
              id={activity.id}
              name={activity.name}
              habit_icon={activity.habit_icon}
              duration_seconds={activity.duration_seconds}
              video_urls={activity.video_urls}
              choice_type={activity.choice_type}
              allowed_focus_mode_id={activity.allowed_focus_mode_id}
              allowed_apps={activity.allowed_apps}
            />
            {allowAddToRoutine && (
              <Tooltip
                message={t(labelKey)}
                icon={
                  <BaseButton
                    onClick={() => handleAdd(activity)}
                    disabled={disabled}
                    size='xs'
                    icon={
                      <PlusCircle
                        styles='~w-3.5/4 h-auto'
                        fill={
                          disabled
                            ? COLOR.PRIMARY.LIGHT_200
                            : COLOR.PRIMARY.LIGHT_50
                        }
                        fillDarkMode={
                          disabled
                            ? COLOR.SECONDARY.DARK_600
                            : COLOR.PRIMARY.DARK_700
                        }
                      />
                    }
                  />
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
export default HabitPackActivityList;
